<template>
  <main v-if="page" class="bh-page">
    <SimpleTopNav />

    <template v-if="page.layout === 'landing'">
      <header class="hero-landing" :style="heroStyle">
        <div class="hero-landing__inner">
          <p class="eyebrow">Backyard Hibachi Pro</p>
          <h1>{{ page.h1 }}</h1>
          <p class="sub">{{ page.sub }}</p>

          <div class="trust-row" aria-label="Service highlights">
            <span>★★★★★ 5.0 Rated on Google</span>
            <span>$50 / Person</span>
            <span>$500 Party Minimum</span>
          </div>

          <a href="/booking" class="btn btn-hero">{{ page.buttonText }}</a>
          <p class="quick-note">Fast Response | Low Deposit | Instant Quote</p>
        </div>
      </header>

      <section class="landing-section service-strip">
        <div class="landing-wrap">
          <h2>{{ page.serviceHeading }}</h2>
          <div class="pill-row">
            <span v-for="area in page.serviceAreas" :key="area">{{ area }}</span>
          </div>
        </div>
      </section>

      <section class="landing-section">
        <div class="landing-wrap">
          <h2>{{ page.featureHeading }}</h2>
          <div class="feature-grid">
            <article v-for="feature in page.features" :key="feature.title" class="feature-card">
              <span class="feature-icon">{{ iconFor(feature.icon) }}</span>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="landing-section pricing-section">
        <div class="landing-wrap">
          <div class="landing-pricing">
            <div class="pricing-head">
              <h2>Standard Hibachi Package</h2>
              <p><strong>$50</strong> / person</p>
            </div>
            <ul>
              <li><strong>Salad:</strong> Fresh green salad served with signature ginger dressing</li>
              <li><strong>Sides:</strong> Hibachi mixed vegetables and homemade fried rice</li>
              <li><strong>2 Protein Choices:</strong> Chicken, steak, shrimp, calamari, tofu, with filet mignon upgrade
                available</li>
              <li><strong>Kids Special:</strong> 2 FREE kids meals included with orders $600+ ($30/kid for extra)</li>
            </ul>
            <a href="sms:6267165717" class="btn btn-quote">Text Us for Instant Quote</a>
          </div>
        </div>
      </section>
    </template>

    <template v-else>
      <header class="hero-city">
        <p class="jp">鉄板焼き</p>
        <h1>Hibachi Catering in<br><em>{{ page.city }}</em></h1>
        <p class="sub">{{ page.sub }}</p>
        <a href="/booking" class="btn">{{ page.buttonText }}</a>
      </header>

      <section>
        <div class="wrap content" v-html="page.content"></div>
      </section>
    </template>

    <div class="cta-bar">
      <h2>{{ ctaTitle }}</h2>
      <a href="tel:6267165717" class="btn">(626) 716-5717</a>
      <a href="/booking" class="btn btn-outline">Book Online</a>
    </div>

    <SimpleFooter :suffix="page.footerSuffix" />
  </main>
  <NotFoundView v-else />
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { cityPageMap } from '@/data/cityPages'
import SimpleTopNav from '@/components/SimpleTopNav.vue'
import SimpleFooter from '@/components/SimpleFooter.vue'
import NotFoundView from './NotFoundView.vue'

const route = useRoute()
const page = computed(() => cityPageMap[route.meta.slug])
const ctaTitle = computed(() => page.value?.ctaTitle || `Ready to Book Hibachi in ${page.value?.city}?`)
const heroStyle = computed(() => {
  if (!page.value?.heroImage) return {}
  return {
    backgroundImage: `linear-gradient(90deg, rgba(8, 8, 8, .9), rgba(8, 8, 8, .62), rgba(8, 8, 8, .9)), url('${page.value.heroImage}')`
  }
})

const iconFor = (name) => ({
  clock: '✓',
  flame: '🔥',
  steak: '◆',
  truck: '✓'
}[name] || '✓')
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.bh-page {
  --gold: #d4a574;
  --gold-light: #f0d4a8;
  font-family: 'Inter', sans-serif;
  background: #080808;
  color: #e8e4df;
  line-height: 1.7;
  overflow-x: hidden;
}

a {
  color: inherit;
  text-decoration: none;
}

.hero-city {
  padding: 8rem 2rem 5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero-city::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 80%, rgba(212, 165, 116, .05) 0%, transparent 60%);
}

.hero-city .jp {
  font-family: 'Noto Serif JP', serif;
  font-size: .8rem;
  color: var(--gold);
  letter-spacing: .5em;
  margin-bottom: 1.5rem;
  opacity: .7;
}

.hero-city h1 {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.15;
}

.hero-city h1 em {
  font-style: normal;
  color: var(--gold);
}

.hero-city .sub {
  color: rgba(255, 255, 255, .5);
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: .95rem;
  line-height: 1.8;
}

.hero-city .btn {
  display: inline-block;
  padding: 1rem 2.5rem;
  background: var(--gold);
  color: #000;
  font-weight: 600;
  font-size: .8rem;
  letter-spacing: .15em;
  text-transform: uppercase;
  transition: all .3s;
}

.hero-city .btn:hover {
  background: var(--gold-light);
  transform: translateY(-2px);
}

.hero-landing {
  min-height: 660px;
  padding: 8.5rem 2rem 5.25rem;
  background-color: #080808;
  background-position: center;
  background-size: cover;
  border-bottom: 4px solid #f59a0a;
  display: grid;
  place-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero-landing::after {
  content: '';
  position: absolute;
  inset: auto 0 0;
  height: 42%;
  background: linear-gradient(180deg, transparent, rgba(8, 8, 8, .88));
}

.hero-landing__inner {
  width: min(1050px, 100%);
  position: relative;
  z-index: 1;
}

.hero-landing .eyebrow {
  color: var(--gold);
  font-size: .82rem;
  font-weight: 800;
  letter-spacing: .28em;
  text-transform: uppercase;
  margin-bottom: 1rem;
}

.hero-landing h1 {
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(2.1rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.08;
  margin: 0 auto 1.25rem;
  max-width: 980px;
}

.hero-landing .sub {
  color: rgba(255, 255, 255, .82);
  font-size: clamp(1rem, 2vw, 1.35rem);
  line-height: 1.55;
  max-width: 900px;
  margin: 0 auto 1.9rem;
}

.trust-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.4rem;
  margin: 0 auto 2rem;
  max-width: 980px;
}

.trust-row span {
  border: 1px solid rgba(255, 255, 255, .22);
  background: rgba(255, 255, 255, .08);
  color: #fff;
  font-weight: 800;
  padding: .75rem 1rem;
  min-height: 48px;
  display: grid;
  place-items: center;
}

.btn-hero,
.btn-quote {
  display: inline-block;
  background: #f59a0a;
  color: #050505;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .09em;
  border-radius: 999px;
  transition: transform .25s, background .25s;
}

.btn-hero {
  min-width: min(520px, 100%);
  padding: 1.25rem 2.2rem;
  font-size: 1.15rem;
}

.btn-hero:hover,
.btn-quote:hover {
  background: var(--gold-light);
  transform: translateY(-2px);
}

.quick-note {
  color: rgba(255, 255, 255, .72);
  margin-top: 1rem;
  font-size: .95rem;
}

.landing-section {
  padding: 4rem 0;
  background: #0b0b0c;
}

.landing-wrap {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 2rem;
}

.landing-section h2 {
  color: #f59a0a;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.25rem, 2.5vw, 1.7rem);
  font-weight: 900;
  letter-spacing: .12em;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 1.7rem;
}

.service-strip {
  padding: 2.7rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
}

.pill-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: .9rem 1.8rem;
}

.pill-row span {
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, .08);
  border-radius: 999px;
  color: rgba(255, 255, 255, .82);
  font-weight: 700;
  min-height: 38px;
  padding: .55rem 1.2rem;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
}

.feature-card {
  background: #15151a;
  border: 1px solid rgba(255, 255, 255, .09);
  border-top: 4px solid #f59a0a;
  border-radius: 8px;
  padding: 2rem 1.5rem 1.7rem;
  text-align: center;
  min-height: 220px;
}

.feature-icon {
  color: #f59a0a;
  display: block;
  font-size: 2rem;
  line-height: 1;
  margin-bottom: 1rem;
}

.feature-card h3 {
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  line-height: 1.25;
  margin: 0 0 .8rem;
}

.feature-card p {
  color: rgba(255, 255, 255, .62);
  font-size: .95rem;
  line-height: 1.65;
}

.pricing-section {
  padding-top: 1.8rem;
}

.landing-pricing {
  border: 2px solid #f59a0a;
  border-radius: 8px;
  background: #121218;
  padding: 2rem;
}

.pricing-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  border-bottom: 1px dashed rgba(255, 255, 255, .14);
  padding-bottom: 1.25rem;
  margin-bottom: 1.3rem;
}

.pricing-head h2 {
  text-align: left;
  margin: 0;
}

.pricing-head p {
  color: rgba(255, 255, 255, .76);
  white-space: nowrap;
  font-size: 1.05rem;
}

.pricing-head strong {
  color: #fff;
  font-size: 2rem;
}

.landing-pricing ul {
  list-style: none;
  margin: 0 0 1.8rem;
}

.landing-pricing li {
  color: rgba(255, 255, 255, .72);
  padding: .45rem 0 .45rem 1.45rem;
  position: relative;
}

.landing-pricing li::before {
  content: '✓';
  color: #f59a0a;
  font-weight: 900;
  position: absolute;
  left: 0;
}

.btn-quote {
  display: block;
  width: min(380px, 100%);
  margin: 0 auto;
  padding: .95rem 1.5rem;
  text-align: center;
}

.wrap {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}

section {
  padding: 5rem 0;
}

.content :deep(h2) {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.6rem;
  margin-bottom: 1.2rem;
  color: #fff;
}

.content :deep(h3) {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.2rem;
  margin: 2rem 0 .8rem;
  color: var(--gold);
}

.content :deep(p) {
  color: rgba(255, 255, 255, .6);
  font-size: .92rem;
  line-height: 1.9;
  margin-bottom: 1.2rem;
}

.content :deep(ul) {
  list-style: none;
  margin: 1rem 0 2rem;
}

.content :deep(ul li) {
  padding: .5rem 0 .5rem 1.5rem;
  position: relative;
  color: rgba(255, 255, 255, .6);
  font-size: .9rem;
}

.content :deep(ul li::before) {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 8px;
  height: 1px;
  background: var(--gold);
  opacity: .6;
}

.content :deep(.pricing-box) {
  background: rgba(255, 255, 255, .02);
  border: 1px solid rgba(255, 255, 255, .06);
  border-radius: 16px;
  padding: 2.5rem;
  margin: 2.5rem 0;
}

.content :deep(.pricing-box h3) {
  margin-top: 0;
}

.content :deep(.areas-list) {
  display: flex;
  flex-wrap: wrap;
  gap: .8rem;
  margin: 1.5rem 0;
}

.content :deep(.areas-list a) {
  padding: .5rem 1.2rem;
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(255, 255, 255, .06);
  border-radius: 20px;
  font-size: .8rem;
  color: rgba(255, 255, 255, .6);
  transition: all .3s;
}

.content :deep(.areas-list a:hover) {
  border-color: var(--gold);
  color: var(--gold);
}

.content :deep(.faq-item) {
  border-bottom: 1px solid rgba(255, 255, 255, .05);
}

.content :deep(.faq-item summary) {
  padding: 1.2rem 0;
  font-size: .92rem;
  cursor: pointer;
  color: rgba(255, 255, 255, .8);
  list-style: none;
  display: flex;
  justify-content: space-between;
}

.content :deep(.faq-item summary::after) {
  content: '+';
  color: var(--gold);
  opacity: .5;
}

.content :deep(.faq-item[open] summary::after) {
  content: '-';
}

.content :deep(.faq-item p) {
  padding: 0 0 1.2rem;
  font-size: .85rem;
  color: rgba(255, 255, 255, .5);
  line-height: 1.8;
}

.cta-bar {
  text-align: center;
  padding: 5rem 2rem;
  background: linear-gradient(160deg, #0c0c0c, #14100c, #0c0c0c);
}

.cta-bar h2 {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  margin-bottom: 1.5rem;
}

.cta-bar .btn {
  display: inline-block;
  padding: 1.1rem 3rem;
  background: var(--gold);
  color: #000;
  font-weight: 600;
  font-size: .9rem;
  letter-spacing: .08em;
  transition: all .3s;
  margin: 0 .5rem;
}

.cta-bar .btn-outline {
  background: transparent;
  border: 1px solid rgba(212, 165, 116, .5);
  color: var(--gold);
}

.cta-bar .btn:hover {
  transform: translateY(-2px);
}

@media (max-width: 760px) {
  .hero-landing {
    min-height: 620px;
    padding: 7.5rem 1.1rem 4rem;
  }

  .trust-row,
  .feature-grid {
    grid-template-columns: 1fr;
  }

  .trust-row {
    gap: .8rem;
  }

  .btn-hero {
    min-width: 0;
    width: 100%;
    font-size: .95rem;
  }

  .landing-wrap {
    padding: 0 1.1rem;
  }

  .pricing-head {
    display: block;
  }

  .pricing-head p {
    margin-top: .8rem;
  }

  .cta-bar .btn {
    display: block;
    max-width: 320px;
    margin: .75rem auto 0;
  }
}
</style>
